/* eslint-disable @typescript-eslint/no-explicit-any */
import { Inject, Injectable, NgZone } from '@angular/core';
import { datadogLogs as datadog } from '@datadog/browser-logs';
import { environment } from 'src/environments/environment';
import mixpanel from 'mixpanel-browser';
import { UserService } from './user.service';
import { LogEvent } from '../enums/log-event';
import { TranslateService } from '@ngx-translate/core';
import { LIVECHAT_VERSION } from 'src/app/shared/utils/livechat_version';

type LogLevel = 'log' | 'error' | 'debug' | 'info' | 'warn';

// Eventos de produto que não precisam ir para o Datadog
const MIXPANEL_ONLY_EVENTS: LogEvent[] = [LogEvent.LIVECHAT_EXPIRED_MEDIA_SHOWN];

const MIXPANEL_EVENTS: LogEvent[] = [
  LogEvent.LIVECHAT_ECONOMY_MODE_TOGGLE,
  ...MIXPANEL_ONLY_EVENTS,
];

@Injectable({
  providedIn: 'root',
})
export class LoggerService {
  private initialized = false;

  constructor(
    private userService: UserService,
    private translateService: TranslateService,
    private ngZone: NgZone,
    @Inject(LIVECHAT_VERSION) private version: string
  ) {
    this.init();
  }

  private init() {
    if (!environment?.externalLogs) {
      return;
    }
    const user = this.userService.getCurrentUser();

    this.ngZone.runOutsideAngular(() => {
      datadog.init(environment.datadog);
      mixpanel.init(environment.mixpanel.projectToken);
      mixpanel.identify(`${user?.id}` || 'undefined');
    })

    this.initialized = true;
  }

  private handleEvent(
    logLevel: LogLevel,
    event: LogEvent,
    data?: Record<string, any> | Error,
  ) {
    try {
      const finalData = this.prepareData(logLevel, data);

      if (this.initialized) {
        if (!MIXPANEL_ONLY_EVENTS.includes(event)) {
          datadog.logger[logLevel](event, finalData);
        }
        if (MIXPANEL_EVENTS.includes(event)) {
          mixpanel.track(event, finalData);
        }
      } else {
        console[logLevel](event, finalData);
      }
    } catch (error) {
      console.error(`couldn't log '${logLevel}'`, error);
    }
  }

  private attachTime(data: any, logLevel: LogLevel){
    return {
      ...data,
      ocurredAt: new Date().toJSON(),
      logLevel,
    }
  }

  private attachUserData(data: any){
    const user = this.userService.getCurrentUser();
    return {
      ...data,
      user: {
        global: user? user.role === 'global': null,
        id: user?.id || null,
        name: user?.name || null,
        email: user?.email || null,
        companyIds: user?.companiesIds || null,
        language: this.translateService.currentLang,
        userRole: user?.userRole,
      },
    };
  }

  private attachVersion(data: any){
    return {
      ...data,
      appVersion: this.version || 'unknown',
    };
  }

  private parseError(err: Error){
    return {
      name: err.name || '',
      stack: err.stack || '',
      messsage: err.message || ''
    };
  }

  private prepareData(logLevel: LogLevel, data?: Record<string, any> | Error) {
    if(data instanceof Error)
      data = this.parseError(data);

    data = this.attachUserData(data);
    data = this.attachTime(data, logLevel);
    data = this.attachVersion(data);
    return data;
  }

  public log(event: LogEvent, data?: Record<string, any>) {
    this.handleEvent('log', event, data);
  }

  public error(event: LogEvent, data?: Error) {
    this.handleEvent('error', event, data);
  }

  public debug(event: LogEvent, data?: Record<string, any>) {
    this.handleEvent('debug', event, data);
  }

  public info(event: LogEvent, data?: Record<string, any>) {
    this.handleEvent('info', event, data);
  }

  public warn(event: LogEvent, data?: Record<string, any>) {
    this.handleEvent('warn', event, data);
  }

  public logWithBeacon(event: LogEvent, data?: Record<string, any> | Error) {
    if (!this.initialized || !environment?.datadog?.clientToken) {
      return;
    }

    try {
      const finalData = this.prepareData('warn', data);

      const payload = JSON.stringify({
        message: event,
        status: 'warn',
        service: environment.datadog.service || 'angular-app',
        ddsource: 'browser',
        ...finalData,
      });

      const ddSite = environment.datadog.site || 'datadoghq.com';
      const ddToken = environment.datadog.clientToken;

      const url = `https://http-intake.logs.${ddSite}/api/v2/logs?dd-api-key=${ddToken}&ddsource=browser&ddtags=version:${this.version}`;

      const success = navigator.sendBeacon(url, payload);

      if (!success) {
        console.warn('LoggerService: Beacon queue full, log might be lost.');
      }
    } catch (e) {
      console.error('LoggerService: Failed to send beacon', e);
    }
  }
}
